/**
 * Felurile de fișiere care se pot atașa unui eveniment, unei campanii sau unei acțiuni.
 *
 * Lista stă în cod, nu în baza de date, și nu se administrează din interfață. Sunt puține și
 * stabile; un ecran de administrare ar cere răspunsuri la întrebări care nu merită puse aici
 * (cine le administrează, ce se întâmplă cu documentele când cineva șterge un fel). Un fel nou
 * se adaugă cu o linie aici.
 *
 * `poza` e primul și rămâne implicit: până în septembrie 2026 modulul ținea doar poze, iar
 * fișierele de atunci sunt toate poze.
 */
export const FELURI_DOCUMENT = [
    'poza',
    'contract',
    'raport',
    'proces_verbal',
    'autorizatie',
    'oferta',
    // Actele clientului (Clientii mei, 20.10.2026).
    'anexa',
    'prestari_servicii',
    'planograma',
    'acord',
    'altul',
];
export const ETICHETE_FEL_DOCUMENT = {
    poza: 'Poze',
    contract: 'Contracte',
    raport: 'Rapoarte',
    proces_verbal: 'Procese-verbale',
    autorizatie: 'Autorizații / avize',
    oferta: 'Oferte / proforme',
    anexa: 'Anexe',
    prestari_servicii: 'Prestări servicii',
    planograma: 'Planograme',
    acord: 'Alte înțelegeri',
    altul: 'Altele',
};
/** Eticheta la singular, pentru un fișier anume. */
export const ETICHETE_FEL_DOCUMENT_SG = {
    poza: 'Poză',
    contract: 'Contract',
    raport: 'Raport',
    proces_verbal: 'Proces-verbal',
    autorizatie: 'Autorizație / aviz',
    oferta: 'Ofertă / proformă',
    anexa: 'Anexă',
    prestari_servicii: 'Prestări servicii',
    planograma: 'Planogramă',
    acord: 'Altă înțelegere',
    altul: 'Alt document',
};
export const FEL_DOCUMENT_IMPLICIT = 'poza';
export function esteFelDocument(v) {
    return FELURI_DOCUMENT.includes(v);
}
/**
 * Pozele se micșorează în browser și se văd ca miniaturi; restul sunt documente, care se
 * descarcă. Diferența asta decide și ce formate se acceptă, și cât de mare poate fi fișierul.
 */
export function esteFelDePoza(fel) {
    return fel === 'poza';
}
/** Formatele primite de server (`apps/api/src/modules/poze/cale.ts`). */
export const MIME_POZA = ['image/jpeg', 'image/png', 'image/webp'];
export const MIME_DOCUMENT = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ...MIME_POZA,
];
/** Documentele nu se micsoreaza; serverul refuza peste atat. */
export const OCTETI_MAX_DOCUMENT_CLIENT = 25 * 1024 * 1024;
/** Fara diacritice si fara majuscule — „Proces-verbal_semnat.PDF" ~ „proces verbal semnat pdf". */
function simplu(s) {
    return s
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ');
}
/*
  Cuvintele dupa care se ghiceste felul, in ordinea in care se verifica. Primul care se potriveste
  castiga: „oferta contract" e mai probabil o oferta de contract decat un contract.
*/
const INDICII = [
    ['proces_verbal', /\b(pv|pvs|proces verbal|procesverbal|predare primire)\b/],
    ['oferta', /\b(oferta|ofertei|proforma|proforme|deviz|quote|offer)\b/],
    ['anexa', /\b(anexa|anexe|anexei|annex)\b/],
    [
        'prestari_servicii',
        /\b(prestari servicii|prestare servicii|contract de servicii|servicii marketing)\b/,
    ],
    ['planograma', /\b(planograma|planograme|planogram)\b/],
    ['contract', /\b(contract|contractul|act aditional|acord|agreement)\b/],
    ['autorizatie', /\b(autorizatie|autorizare|aviz|avizul|acord isu|permis)\b/],
    ['raport', /\b(raport|report|rezultate|statistici)\b/],
    ['acord', /\b(intelegere|intelegeri|minuta|acord pe mail)\b/],
];
/**
 * Felurile de acte ale unui client, in ordinea din ecran (Victor, 19.10.2026: „planograme,
 * contracte, anexe, alte intelegeri"; Adrian: „prestari servicii"). Pozele stau separat.
 */
export const FELURI_ACT_CLIENT = [
    'contract',
    'anexa',
    'prestari_servicii',
    'planograma',
    'acord',
    'altul',
];
/** Interactiunile notate de om in fisa clientului. */
export const FELURI_INTERACTIUNE = ['vizita', 'telefon', 'email', 'intalnire', 'alta'];
export const ETICHETE_INTERACTIUNE = {
    vizita: 'Vizită',
    telefon: 'Telefon',
    email: 'Email',
    intalnire: 'Întâlnire',
    alta: 'Altceva',
};
/** Cu cate zile inainte de expirare se anunta un act (agentul si cine l-a adaugat). */
export const ZILE_ANUNT_EXPIRARE_ACT = 30;
/**
 * Felul unui fisier, ghicit din nume si tip — se poate schimba inainte de incarcare.
 *
 * Pana pe 15.10.2026 felul se alegea INAINTE de fisiere, dintr-o lista, si toate fisierele dintr-o
 * incarcare primeau acelasi fel. Cine trantea in fereastra o poza si un contract trebuia sa faca
 * doua incarcari.
 *
 * O imagine e poza, doar daca numele nu spune altceva: „contract semnat.jpg" e un contract
 * scanat, nu o poza de la stand.
 */
export function ghicesteFelDocument(nume, mime) {
    const n = simplu(nume.replace(/\.[a-z0-9]+$/i, ''));
    for (const [fel, re] of INDICII)
        if (re.test(n))
            return fel;
    return MIME_POZA.includes(mime) ? 'poza' : 'altul';
}
/**
 * De ce nu se poate incarca un fisier, in cuvinte — sau `null` daca se poate. Aceleasi reguli ca
 * serverul, ca omul sa afle inainte, nu dupa ce a asteptat urcarea.
 */
export function deCeNuSeIncarca(fel, mime, octeti) {
    if (octeti === 0)
        return 'fișierul e gol';
    if (fel === 'poza') {
        if (!MIME_POZA.includes(mime))
            return 'ca poză merg doar JPG, PNG sau WEBP — alege alt fel sau convertește fișierul';
        return null;
    }
    if (!MIME_DOCUMENT.includes(mime))
        return 'format neacceptat — merg PDF, Word, Excel sau imagini JPG/PNG/WEBP';
    if (octeti > OCTETI_MAX_DOCUMENT_CLIENT)
        return 'peste 25 MB';
    return null;
}
//# sourceMappingURL=documente.js.map